/** @jsx jsx */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import {
  Modal as DlsModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@americanexpress/dls-react';
import { FxMultiButtons } from '../FxButtons/FxMultiButtons';

import { setBodyStylesOnModalOpen, setBodyStylesOnModalClose } from '../../utils/modalUtils';

import { size as sizes } from '../../constants';

import {
  modalContainerExtraLarge,
  modalContainerLarge,
  modalContainerMedium,
  modalContainerSmall,
  modalOutline,
} from './styles';
import { flexDirectionType, justifyContentType } from '../../constants/styles';

const mapSizeToModalContainerStyle = {
  [sizes.Small]: modalContainerSmall,
  [sizes.Medium]: modalContainerMedium,
  [sizes.Large]: modalContainerLarge,
  [sizes.XtraLarge]: modalContainerExtraLarge,
};
export const FxModal = ({
  id,
  modalHeading,
  modalBody,
  onClose,
  size,
  theme,
  primaryButtonTheme,
  primaryButtonLabel,
  onPrimaryButtonClick,
  isDisablePrimaryButton,
  secondaryButtonTheme,
  secondaryButtonLabel,
  secondaryBtnClass,
  onSecondaryButtonClick,
  justifyButtons,
  flexDirection,
  parentPrimaryButton,
}) => {
  /* Max Height for FxModal is 80vh.
     Default height of Header including Padding is 54px and Footer is 91px.
     Remove that from FxModal height to get Max Height for FxModal body.
  */
  const [modalBodyMaxHeight, setModalBodyMaxHeight] = useState('calc(80vh - 145px)');

  useEffect(() => {
    // set the body styles when FxModal opens to prevent background scrolling
    setBodyStylesOnModalOpen();

    const modalHeadingEl = document.getElementById('modal-heading');
    // Calculate the height of FxModal Heading.
    const height = modalHeadingEl?.clientHeight || 24;

    /* Max Height for FxModal is 80vh.
       Default Padding of Header is 15px on top and bottom and Footer Height is 91px.
       Remove that from FxModal height along with height
       of the header computed above to get Max Height for FxModal body.
    */
    setModalBodyMaxHeight(`calc(80vh - ${height + 121}px)`);

    // set the body styles when FxModal closes to reset background styles applied when FxModal was opened
    return () => setBodyStylesOnModalClose();
  }, []);

  return (
    <DlsModal
      id={id}
      theme={{
          ...modalOutline,
          ...theme,
          '& > div': mapSizeToModalContainerStyle[size],
          '& > div > div[role=dialog]': {
            overflowY: 'unset',
          },
        }}
    >
      <ModalHeader onClose={onClose} closeButtonLabel="Close">
        <h2 id="modal-heading" className="fluid heading-3">{modalHeading}</h2>
      </ModalHeader>
      <ModalBody theme={{ maxHeight: modalBodyMaxHeight, overflowY: 'auto' }}>{modalBody}</ModalBody>
      {((primaryButtonLabel && onPrimaryButtonClick)
          || parentPrimaryButton
          || (secondaryButtonLabel && onSecondaryButtonClick)) && (
          <ModalFooter>
            <FxMultiButtons
              id={id}
              primaryButtonTheme={primaryButtonTheme}
              primaryButtonLabel={primaryButtonLabel}
              onPrimaryButtonClick={onPrimaryButtonClick}
              isDisablePrimaryButton={isDisablePrimaryButton}
              secondaryButtonTheme={secondaryButtonTheme}
              secondaryButtonLabel={secondaryButtonLabel}
              secondaryBtnClass={secondaryBtnClass}
              onSecondaryButtonClick={onSecondaryButtonClick}
              justifyButtons={justifyButtons}
              flexDirection={flexDirection}
              parentPrimaryButton={parentPrimaryButton}
            />
          </ModalFooter>
        )}
    </DlsModal>
    );
};

FxModal.propTypes = {
  id: PropTypes.string,
  modalHeading: PropTypes.string.isRequired,
  modalBody: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  size: PropTypes.oneOf(Object.values(sizes)),
  theme: PropTypes.shape({}),
  primaryButtonTheme: PropTypes.shape({}),
  primaryButtonLabel: PropTypes.string,
  onPrimaryButtonClick: PropTypes.func,
  isDisablePrimaryButton: PropTypes.bool,
  secondaryButtonTheme: PropTypes.shape({}),
  secondaryButtonLabel: PropTypes.string,
  onSecondaryButtonClick: PropTypes.func,
  secondaryBtnClass: PropTypes.string,
  justifyButtons: PropTypes.string,
  flexDirection: PropTypes.string,
  parentPrimaryButton: PropTypes.element,
};

FxModal.defaultProps = {
  id: 'modal',
  size: sizes.Medium,
  theme: {},
  primaryButtonTheme: {},
  primaryButtonLabel: '',
  onPrimaryButtonClick: null,
  isDisablePrimaryButton: false,
  secondaryButtonTheme: {},
  secondaryButtonLabel: '',
  secondaryBtnClass: 'margin-responsive-r',
  onSecondaryButtonClick: null,
  justifyButtons: justifyContentType.CENTER,
  flexDirection: flexDirectionType.ROW,
  parentPrimaryButton: null,
};
